import { defineBlock } from "eddev/blocks"
import { cn } from "@utils/tw"
import { Col, Container, Grid } from "@components/layout/Grid"
import { ComponentPropsWithoutRef, forwardRef, useState } from "react"
import { Typography } from "@components/atoms/Typography"
import { VSpace } from "@components/layout/Spacer"
import { Button } from "@components/atoms/Button"
import { CARET_RIGHT } from "@components/atoms/Icons"
import { Accordion } from "@components/molecules/Accordion"
import { Chip } from "@components/atoms/Chip"
import { IDPBranchDropdown } from "@components/atoms/IDPBranchDropdown"

export const meta: BlockMeta = {
  title: "Multi Dropdown Widget",
  category: "common",
  icon: "welcome-widgets-menus",
  keywords: ["multi"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/multi-dropdown-widget", (props) => {
  const [activeLeft, setActiveLeft] = useState<number>(0)
  const [activeRight, setActiveRight] = useState<number>(0)

  return (
    <WidgetContainer>
      <Grid>
        <Col initial={12}>
          <Widget>
            <Left>
              <ul>
                {props.leftSide?.sections?.map((section: any, i) => (
                  <li key={i}>
                    <button onClick={() => setActiveLeft(i)} className={activeLeft === i ? `active` : ``}>
                      <span>
                        {CARET_RIGHT} {section?.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {props.leftSide?.sections && (
                <>
                  {props.leftSide.sections[activeLeft]?.faqsText ? (
                    <TextContainer className="text-container">
                      <Typography
                        as="div"
                        variant="text-m"
                        dangerouslySetInnerHTML={{ __html: props.leftSide.sections[activeLeft]?.content ?? "" }}
                      />
                    </TextContainer>
                  ) : (
                    <AccordionContainer>
                      {props.leftSide.sections[activeLeft]?.faq?.map((pair, i) => (
                        <Accordion key={i} title={pair?.question} content={pair?.answer} />
                      ))}
                    </AccordionContainer>
                  )}
                </>
              )}
            </Left>
            <Right>
              <div>
                {props.rightSide?.chipText && (
                  <>
                    <Chip color="black">{props.rightSide.chipText}</Chip>
                    <VSpace initial={7} />
                  </>
                )}
                <ul>
                  {props.rightSide?.sections?.map((section, i) => (
                    <li key={i}>
                      <button onClick={() => setActiveRight(i)} className={activeRight === i ? `active` : ``}>
                        <span>
                          {CARET_RIGHT} {section?.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <VSpace initial={7} />
                {props.rightSide?.sections && (
                  <>
                    <hr />
                    <Typography
                      as="div"
                      variant="text-m"
                      dangerouslySetInnerHTML={{ __html: props.rightSide.sections[activeRight]?.content ?? "" }}
                    />
                  </>
                )}
              </div>

              {props.rightSide?.sections && (
                <>
                  {props.rightSide.sections[activeRight]?.useBranchFinderWidget ? (
                    <>
                      <VSpace initial={7} />
                      <IDPBranchDropdown medium />
                    </>
                  ) : (
                    <>
                      {props.rightSide.sections[activeRight]?.button && (
                        <>
                          <VSpace initial={7} />
                          <Button
                            size="medium"
                            color="yellow"
                            href={props.rightSide.sections[activeRight]!.button?.url ?? ""}
                            target={props.rightSide.sections[activeRight]?.button?.target ? "_blank" : undefined}
                            icon={
                              props.rightSide.sections[activeRight]?.button?.target ? "ARROW_UP_RIGHT" : "ARROW_RIGHT"
                            }
                            hSpacing="between"
                            iconStyle="right"
                          >
                            {props.rightSide.sections[activeRight]?.button?.title}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Right>
          </Widget>
        </Col>
      </Grid>
    </WidgetContainer>
  )
})

const WidgetContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn("my-5 [&_div>button.button]:w-full [&_div>button.button]:box-border", className)}
      {...props}
    />
  ),
)
WidgetContainer.displayName = "WidgetContainer"

const Widget = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-grey py-6 px-7 rounded-md flex flex-col gap-9 h-full md:flex-row md:items-start md:justify-between",
      className,
    )}
    {...props}
  />
))
Widget.displayName = "Widget"

// Mirrors the original stitches selector tree 1:1 (descendant combinators,
// same order) for `ul > li > button > span > svg`, plus the sibling
// `.text-container ul`/`li` override — see multi-section-widget.tsx for the
// same shape reused with a different subtitle heading.
const LEFT_BASE = [
  "w-full h-full flex flex-col justify-between",
  "[&_div>p]:my-3",
  "[&_ul]:m-0 [&_ul]:p-0",
  "[&_ul_li]:list-none [&_ul_li]:p-0",
  "[&_ul_li_button]:appearance-none [&_ul_li_button]:bg-transparent [&_ul_li_button]:flex [&_ul_li_button]:items-center [&_ul_li_button]:type-title-l [&_ul_li_button]:text-mid-grey [&_ul_li_button]:cursor-pointer [&_ul_li_button]:transition-all [&_ul_li_button]:ease-movement [&_ul_li_button]:duration-300 [&_ul_li_button]:overflow-hidden [&_ul_li_button]:text-left",
  "[&_ul_li_button_span]:flex [&_ul_li_button_span]:items-start [&_ul_li_button_span]:-translate-x-[32px] [&_ul_li_button_span]:transition-all [&_ul_li_button_span]:ease-movement [&_ul_li_button_span]:duration-300",
  "[&_ul_li_button_span_svg]:mt-[4px] [&_ul_li_button_span_svg]:min-w-[32px] [&_ul_li_button_span_svg]:min-h-[32px] [&_ul_li_button_span_svg]:w-[32px] [&_ul_li_button_span_svg]:h-[32px]",
  "[&_ul_li_button.active]:text-black [&_ul_li_button.active_span]:translate-x-0",
  "[&_ul_li_button:hover]:text-dark-grey [&_ul_li_button:hover.active]:text-black",
  "[&_ul_li_button_svg]:w-[32px] [&_ul_li_button_svg]:h-[32px]",
  "[&_.text-container_ul]:pl-5 [&_.text-container_ul_li]:list-disc",
  "md:w-[calc(100%*(2/3))]",
]

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(LEFT_BASE, className)} {...props} />
))
Left.displayName = "Left"

const RIGHT_BASE = [
  "w-full bg-white rounded-md p-5 flex flex-col justify-between self-stretch",
  "[&_div>p]:my-3",
  "[&_ul]:m-0 [&_ul]:p-0",
  "[&_ul_li]:list-none [&_ul_li]:p-0",
  "[&_ul_li_button]:appearance-none [&_ul_li_button]:bg-transparent [&_ul_li_button]:flex [&_ul_li_button]:items-center [&_ul_li_button]:type-title-m [&_ul_li_button]:text-mid-grey [&_ul_li_button]:cursor-pointer [&_ul_li_button]:transition-all [&_ul_li_button]:ease-movement [&_ul_li_button]:duration-300 [&_ul_li_button]:overflow-hidden",
  "[&_ul_li_button_span]:flex [&_ul_li_button_span]:items-start [&_ul_li_button_span]:-translate-x-[24px] [&_ul_li_button_span]:transition-all [&_ul_li_button_span]:ease-movement [&_ul_li_button_span]:duration-300",
  "[&_ul_li_button_span_svg]:mt-[5px] [&_ul_li_button_span_svg]:w-[24px] [&_ul_li_button_span_svg]:h-[24px] [&_ul_li_button_span_svg]:min-w-[24px] [&_ul_li_button_span_svg]:min-h-[24px]",
  "[&_ul_li_button.active]:text-black [&_ul_li_button.active_span]:translate-x-0",
  "[&_ul_li_button:hover]:text-dark-grey [&_ul_li_button:hover.active]:text-black",
  "[&_ul_li_button_svg]:w-[24px] [&_ul_li_button_svg]:h-[24px]",
  "[&_hr]:border-0 [&_hr]:border-t [&_hr]:border-t-light-grey [&_hr]:mb-4",
  "[&_a_span]:grow [&_button_span]:grow [&_a_span]:text-left [&_button_span]:text-left [&_a_span]:font-semibold [&_button_span]:font-semibold",
  "md:w-[calc(100%*(1/3))]",
]

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(RIGHT_BASE, className)} {...props} />
))
Right.displayName = "Right"

const TextContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col mt-[50px] md:mt-[130px]", className)} {...props} />
))
TextContainer.displayName = "TextContainer"

const AccordionContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col border-b border-b-grey mt-[50px] [&>div]:border-t [&>div]:border-t-grey md:mt-[130px]",
        className,
      )}
      {...props}
    />
  ),
)
AccordionContainer.displayName = "AccordionContainer"
